// frontend/src/hooks/usePurchaseOrderCreated.js
//
// What every screen does once PurchaseOrderForm has saved a NEW purchase
// order: the saved PO opens in its own details tab, so the outcome goes to
// the shell toast (it outlives the page the form was on), and the change is
// announced (`queries/dataChanged`) — a new order is stock on the way and an
// open order, one created from a recommendation consumes it, and a line's
// cost can become the supplier's price and the item's cost of record. PO save
// lives in usePurchaseOrderDraft, which saves through sessionApi outside
// TanStack, so the announcement is wired here.

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from '../components/common/appToastBus';
import { DATA, useDataChanged } from '../queries/dataChanged';
import { useOpenPurchaseOrder } from './useOpenPurchaseOrder';

/**
 * @returns {({ id, poNumber, status, messages }) => void} PurchaseOrderForm's
 *   `onSaved` for a create.
 */
export function usePurchaseOrderCreated() {
  const { t } = useTranslation();
  const changed = useDataChanged();
  const openPurchaseOrder = useOpenPurchaseOrder();
  return useCallback(({ id, poNumber, status, messages }) => {
    const statusLabel = status === 'draft'
      ? t('purchaseOrders.create.createdAsDraft')
      : t('purchaseOrders.create.createdAndIssued');
    showToast([`${statusLabel}: ${poNumber}`, ...messages].join(' | '), 'success');
    openPurchaseOrder(id);
    changed([DATA.purchaseOrders, DATA.recommendations, DATA.items, DATA.supplierLinks]);
  }, [t, changed, openPurchaseOrder]);
}
